import { defineStore } from 'pinia'
import { ref } from 'vue'
import { 
  Profile,
  getProfiles,
  createProfile,
  updateProfile,
  deleteProfile
} from '../firebase/services/profiles.service'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { db } from '../firebase/config'


export const useProfileStore = defineStore('profile', () => {
  const profiles = ref<Profile[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const auth = getAuth()

  const fetchProfiles = async () => {
    try {
      // Verificar se está autenticado
      if (!auth.currentUser) {
        console.log('[Profile Store] Usuário não autenticado, ignorando carregamento');
        profiles.value = [];
        return;
      }

      loading.value = true;
      error.value = null;

      const profilesRef = collection(db, 'profiles');
      const q = query(profilesRef, where('active', '==', true));
      const snapshot = await getDocs(q);
      
      profiles.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Profile[];

    } catch (e) {
      console.error('[Profile Store] Erro ao carregar perfis:', e);
      error.value = e instanceof Error ? e.message : 'Erro desconhecido';
      profiles.value = [];
    } finally {
      loading.value = false;
    }
  }

  // Observar mudanças de autenticação
  onAuthStateChanged(auth, (user) => {
    if (!user) {
      profiles.value = [];
    }
  });

  async function addProfile(profile: Omit<Profile, 'id' | 'active' | 'createdAt' | 'updatedAt'>) {
    try {
      loading.value = true
      const newProfile = await createProfile(profile)
      profiles.value.push(newProfile)
      return true
    } catch (e) {
      error.value = 'Error adding profile'
      console.error(e)
      return false
    } finally {
      loading.value = false
    }
  }

  async function updateProfileData(id: string, data: Partial<Profile>) {
    try {
      loading.value = true
      const updatedProfile = await updateProfile(id, data)
      const index = profiles.value.findIndex(p => p.id === id)
      if (index !== -1) {
        profiles.value[index] = { ...profiles.value[index], ...updatedProfile }
      }
      return true
    } catch (e) {
      error.value = 'Error updating profile'
      console.error(e)
      return false
    } finally {
      loading.value = false
    }
  }

  async function deleteProfileById(id: string) {
    try {
      loading.value = true
      await deleteProfile(id)
      profiles.value = profiles.value.filter(p => p.id !== id)
      return true
    } catch (e) {
      error.value = 'Error deleting profile'
      console.error(e)
      return false
    } finally {
      loading.value = false
    }
  }

  return {
    profiles,
    loading,
    error,
    fetchProfiles,
    addProfile,
    updateProfile: updateProfileData,
    deleteProfile: deleteProfileById
  }
})